import * as React from 'react';
import { useCurrentUser } from '@hetivo/hetivo-lib-front-utils';
import type { CurrentUserResponse, ResolvedUserScope } from '@hetivo/hetivo-lib-types';
import { useMasterContext } from '&/master';
import { useUserScopes } from '&/hooks/use-user-scopes';
import { useAuth } from '&/auth';
import { useLocation } from '../settings/locations/hooks/use-location';
import { useTerritory } from '../settings/territories/hooks/use-territory';
import { MainSelectorBreadcrumbView } from './components/breadcrumb';
import { MainSelectorDropdown } from './components/dropdown';
import { useTenantProfile } from './hooks/use-tenant-profile';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MainSelectorViewProps {
  backendUser: CurrentUserResponse | null;
  canReadLocations: boolean;
  canReadTerritories: boolean;
  hasMultipleSites: boolean;
  hasMultipleTerritories: boolean;
  locationColor?: string;
  locationName?: string;
  singleSiteId: string | null;
  singleTerritoryId: string | null;
  tenantName: string;
  territoryName?: string;
}

interface MainSelectorData {
  backendUser: CurrentUserResponse | null;
  canReadLocations: boolean;
  canReadTerritories: boolean;
  hasMultipleSites: boolean;
  hasMultipleTerritories: boolean;
  isTenantLoading: boolean;
  locationColor?: string;
  locationName?: string;
  singleSiteId: string | null;
  singleTerritoryId: string | null;
  tenantName?: string;
  territoryName?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function handleTriggerKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    event.currentTarget.click();
  }
}

/**
 * Finds a territory name from resolved scopes — checks for a direct territory-level match first,
 * then falls back to parentTerritoryName from a location-level scope.
 */
function findTerritoryNameInScopes(scopes: ResolvedUserScope[], territoryId: string): string | undefined {
  const directScope = scopes.find((scope) => scope.id === territoryId && scope.level === 'territory');
  if (directScope) return directScope.name;
  const parentScope = scopes.find((scope) => scope.parentTerritoryId === territoryId);
  return parentScope?.parentTerritoryName;
}

/**
 * Derives the territory name from a user's resolvedScopes.
 * Works for all roles including drivers/carriers who lack territories:list.
 */
function resolveTerritoryNameFromScopes(
  backendUser: CurrentUserResponse | null,
  territoryId: string | null,
): string | undefined {
  if (!backendUser?.resolvedScopes || !territoryId) return undefined;
  return findTerritoryNameInScopes(backendUser.resolvedScopes, territoryId);
}

/**
 * Derives the location name from a user's resolvedScopes.
 */
function resolveLocationNameFromScopes(
  backendUser: CurrentUserResponse | null,
  locationId: string | null,
): string | undefined {
  if (!backendUser?.resolvedScopes || !locationId) return undefined;
  const locationScope = backendUser.resolvedScopes.find(
    (scope) => scope.id === locationId && scope.level === 'location',
  );
  return locationScope?.name;
}

// ─── Data hook ────────────────────────────────────────────────────────────────

interface DeriveNamesParams {
  backendUser: CurrentUserResponse | null;
  locationApiName: string | undefined;
  locationId: string | null;
  territoryApiName: string | undefined;
  territoryId: string | null;
}

function deriveNames({ backendUser, locationApiName, locationId, territoryApiName, territoryId }: DeriveNamesParams): {
  locationName?: string;
  territoryName?: string;
} {
  const territoryName = territoryApiName ?? resolveTerritoryNameFromScopes(backendUser, territoryId);
  const locationName = locationApiName ?? resolveLocationNameFromScopes(backendUser, locationId);
  return { locationName, territoryName };
}

function useMainSelectorData(isAuthenticated: boolean): MainSelectorData {
  const { user: backendUser } = useCurrentUser(isAuthenticated);
  const resolvedUser = backendUser ?? null;
  const { siteId: locationId, territoryId } = useMasterContext();
  const resolvedTerritoryId = territoryId ?? '';
  const { data: tenantProfile, isLoading: isTenantLoading } = useTenantProfile();
  const {
    canAccessSites: canReadLocations,
    canAccessTerritories: canReadTerritories,
    hasMultipleSites,
    hasMultipleTerritories,
    singleSiteId,
    singleTerritoryId,
  } = useUserScopes(isAuthenticated ? resolvedUser : null);
  const { data: locationData } = useLocation(locationId, canReadLocations);
  const { data: territory } = useTerritory(resolvedTerritoryId, canReadTerritories);

  // Territory name derived from resolvedScopes — works for all roles.
  // Location name prefers API data (for users with locations:list), falls back to resolvedScopes.
  const { locationName, territoryName } = deriveNames({
    backendUser: resolvedUser,
    locationApiName: locationData?.name,
    locationId,
    territoryApiName: territory?.name,
    territoryId,
  });

  return {
    backendUser: resolvedUser,
    canReadLocations,
    canReadTerritories,
    hasMultipleSites,
    hasMultipleTerritories,
    isTenantLoading,
    locationColor: locationData?.color,
    locationName,
    singleSiteId,
    singleTerritoryId,
    tenantName: tenantProfile?.name,
    territoryName,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MainSelector(): React.JSX.Element | null {
  const { isAuthenticated } = useAuth();
  const data = useMainSelectorData(isAuthenticated);

  if (data.isTenantLoading || !data.tenantName) {
    return null;
  }

  return (
    <MainSelectorView
      backendUser={data.backendUser}
      canReadLocations={data.canReadLocations}
      canReadTerritories={data.canReadTerritories}
      hasMultipleSites={data.hasMultipleSites}
      hasMultipleTerritories={data.hasMultipleTerritories}
      locationColor={data.locationColor}
      locationName={data.locationName}
      singleSiteId={data.singleSiteId}
      singleTerritoryId={data.singleTerritoryId}
      tenantName={data.tenantName}
      territoryName={data.territoryName}
    />
  );
}

// ─── Private view component ───────────────────────────────────────────────────

function MainSelectorView({
  backendUser,
  canReadLocations,
  canReadTerritories,
  hasMultipleSites,
  hasMultipleTerritories,
  locationColor,
  locationName,
  singleSiteId,
  singleTerritoryId,
  tenantName,
  territoryName,
}: MainSelectorViewProps): React.JSX.Element {
  // Single-location user: non-interactive breadcrumb — purely informational, no dropdown
  if (!hasMultipleTerritories && !hasMultipleSites && singleSiteId) {
    return (
      <MainSelectorBreadcrumbView
        interactive={false}
        locationColor={locationColor}
        locationName={locationName}
        tenantName={tenantName}
        territoryName={territoryName}
      />
    );
  }

  // Single-territory user who cannot browse locations (e.g. carrier/driver with territory-level scope):
  // non-interactive breadcrumb — they have no location selection capability.
  if (!hasMultipleTerritories && !canReadLocations && singleTerritoryId && !singleSiteId) {
    return (
      <MainSelectorBreadcrumbView
        interactive={false}
        locationColor={locationColor}
        locationName={locationName}
        tenantName={tenantName}
        territoryName={territoryName}
      />
    );
  }

  return (
    <MainSelectorDropdown
      backendUser={backendUser}
      canReadLocations={canReadLocations}
      canReadTerritories={canReadTerritories}
      hasMultipleTerritories={hasMultipleTerritories}
      singleTerritoryId={singleTerritoryId}
      territoryName={territoryName}
    >
      <div className="group/trigger" onKeyDown={handleTriggerKeyDown} role="button" tabIndex={0}>
        <MainSelectorBreadcrumbView
          locationColor={locationColor}
          locationName={locationName}
          tenantName={tenantName}
          territoryName={territoryName}
        />
      </div>
    </MainSelectorDropdown>
  );
}
